import { useEffect, useRef, useState } from "react";
import { MapPin, Crown } from "@phosphor-icons/react";

/**
 * IndiaNetworkMap — abstract "network map" visualisation of our offices.
 *
 * It does NOT render a real India outline (that would require either a heavy
 * topo JSON or a copyrighted SVG). Instead we render a stylised dark canvas
 * with a subtle dotted grid, then position each office as a glowing pulse
 * dot using normalised lat/lng coordinates. Animated dashed "tracer" lines
 * flow from the head office to every branch, evoking a logistics network.
 *
 * Clicking a city dot calls onSelectOffice(index) so the parent can scroll
 * to the matching card and flash-highlight it.
 *
 * Built with zero external libs — pure SVG + CSS keyframes already defined
 * in index.css.
 */
const INDIA_BOUNDS = { minLat: 8.0, maxLat: 35.0, minLng: 68.0, maxLng: 97.0 };

// Fallback city → coordinate lookup used when an office doesn't carry coords.
const CITY_LOOKUP = [
  { match: /baddi|solan|himachal/i,             lat: 30.96, lng: 76.79 },
  { match: /mumbai|maharashtra|goregaon/i,      lat: 19.07, lng: 72.87 },
  { match: /bengaluru|bangalore|karnataka/i,    lat: 12.97, lng: 77.59 },
  { match: /gurugram|gurgaon|delhi|haryana|ncr/i, lat: 28.46, lng: 77.03 },
  { match: /chennai|tamil/i,                    lat: 13.08, lng: 80.27 },
  { match: /hyderabad|telangana/i,              lat: 17.38, lng: 78.49 },
  { match: /kolkata|bengal/i,                   lat: 22.57, lng: 88.36 },
  { match: /pune/i,                             lat: 18.52, lng: 73.85 },
  { match: /ahmedabad|gujarat/i,                lat: 23.02, lng: 72.57 },
  { match: /jaipur|rajasthan/i,                 lat: 26.91, lng: 75.79 },
];

function inferCoords(office) {
  if (office.lat && office.lng) return { lat: office.lat, lng: office.lng };
  const blob = `${office.name || office.title || ""} ${office.address || ""}`;
  for (const c of CITY_LOOKUP) if (c.match.test(blob)) return { lat: c.lat, lng: c.lng };
  // Centre of India as a last resort
  return { lat: 22.5, lng: 79.0 };
}

function project(lat, lng, w, h) {
  const { minLat, maxLat, minLng, maxLng } = INDIA_BOUNDS;
  const x = ((lng - minLng) / (maxLng - minLng)) * w;
  const y = (1 - (lat - minLat) / (maxLat - minLat)) * h;
  return { x, y };
}

export default function IndiaNetworkMap({ offices = [], activeIndex = -1, onSelectOffice }) {
  const W = 600;
  const H = 700;
  const containerRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  if (!offices.length) return null;

  const rawProjected = offices.map((o, i) => {
    const { lat, lng } = inferCoords(o);
    const { x, y } = project(lat, lng, W, H);
    return { ...o, idx: i, x, y };
  });

  // De-collide: two offices at nearly-identical lat/lng (e.g. Manufacturing
  // Units I & II both at IIE-SIDCUL Haridwar) project to the same pixel, which
  // makes both the dots AND labels overlap. Fan out any dot that lands within
  // MIN_SEP pixels of another using a fixed downward offset — stacks
  // co-located pins cleanly one above the other with room for two label lines.
  const MIN_SEP = 60;
  const projected = rawProjected.map((o) => ({ ...o }));
  for (let i = 0; i < projected.length; i++) {
    for (let j = 0; j < i; j++) {
      const dx = projected[i].x - projected[j].x;
      const dy = projected[i].y - projected[j].y;
      const dist = Math.hypot(dx, dy);
      if (dist < MIN_SEP) {
        // Push downward + slightly right so the label doesn't collide with
        // the previous pin's stacked (name + role) label pair (~30px tall).
        projected[i].x = projected[j].x + 8;
        projected[i].y = projected[j].y + MIN_SEP;
      }
    }
  }

  // Precompute label anchors: place labels to the RIGHT of the dot by default,
  // but flip to LEFT if that would overflow the SVG viewport, and shift labels
  // that would still collide with a nearby dot's label up/down by 22px.
  const projectedWithLabels = projected.map((o, i, arr) => {
    const flipLeft = o.x > W - 160;
    let anchor = flipLeft ? "end" : "start";
    let labelX = flipLeft ? o.x - 14 : o.x + 14;
    let labelY = o.y + 4;
    // Nudge label vertically if another dot's label sits within 24px
    for (let j = 0; j < i; j++) {
      const other = arr[j];
      const otherLabelY = other.y + 4;
      const otherLabelX = other.x > W - 160 ? other.x - 14 : other.x + 14;
      if (Math.abs(labelY - otherLabelY) < 24 && Math.abs(labelX - otherLabelX) < 120) {
        labelY = otherLabelY + 30;
      }
    }
    return { ...o, labelX, labelY, anchor };
  });

  const head = projected.find((o) => o.is_head_office) || projected[0];
  const branches = projected.filter((o) => o.idx !== head.idx);

  return (
    <div ref={containerRef} className="relative aspect-[6/7] w-full max-w-md mx-auto" data-testid="india-network-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full">
        <defs>
          {/* Dotted-grid background */}
          <pattern id="dotGrid" width="28" height="28" patternUnits="userSpaceOnUse">
            <circle cx="1.2" cy="1.2" r="1.2" fill="rgba(251,146,60,0.18)" />
          </pattern>
          {/* Glow filter for dots */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Tracer gradient (saffron → sky blue) */}
          <linearGradient id="tracerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FB923C" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {/* Background plate */}
        <rect width={W} height={H} fill="url(#dotGrid)" rx="24" />

        {/* Soft ambient blobs */}
        <ellipse cx={W * 0.25} cy={H * 0.25} rx="120" ry="120" fill="rgba(194,65,12,0.10)" />
        <ellipse cx={W * 0.75} cy={H * 0.75} rx="140" ry="140" fill="rgba(14,165,233,0.10)" />

        {/* Stylised India "boundary" — a soft saffron blob hint, not geographically accurate */}
        <path
          d="M 200 60 C 320 40, 420 80, 470 180 C 510 280, 530 380, 480 480 C 430 580, 360 640, 280 640 C 200 640, 140 580, 110 460 C 90 360, 90 240, 130 160 C 160 100, 180 70, 200 60 Z"
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="2"
          strokeDasharray="4 8"
        />

        {/* Tracer lines: head → each branch */}
        {branches.map((b, i) => {
          // Smooth quadratic curve via a midpoint perpendicular offset
          const mx = (head.x + b.x) / 2;
          const my = (head.y + b.y) / 2;
          const dx = b.x - head.x;
          const dy = b.y - head.y;
          const len = Math.hypot(dx, dy) || 1;
          const offset = Math.min(60, len * 0.18);
          const cx = mx + (-dy / len) * offset;
          const cy = my + (dx / len) * offset;
          const dPath = `M ${head.x} ${head.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
          return (
            <g key={`tracer-${b.idx}`}>
              <path d={dPath} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1.4" />
              <path
                d={dPath}
                fill="none"
                stroke="url(#tracerGrad)"
                strokeWidth="2"
                strokeDasharray="6 8"
                strokeLinecap="round"
                style={{
                  animation: mounted ? `dashMove 6s linear ${i * 0.5}s infinite` : "none",
                  opacity: mounted ? 1 : 0,
                  transition: "opacity 700ms",
                }}
              />
            </g>
          );
        })}

        {/* Dots */}
        {projected.map((o, i) => {
          const isActive = i === activeIndex;
          const isHead = o.is_head_office;
          const r = isHead ? 9 : 6.5;
          const fill = isHead ? "#FB923C" : "#0EA5E9";
          return (
            <g
              key={`dot-${i}`}
              transform={`translate(${o.x},${o.y})`}
              onClick={() => onSelectOffice && onSelectOffice(i)}
              style={{
                cursor: "pointer",
                opacity: mounted ? 1 : 0,
                transform: mounted ? `translate(${o.x}px, ${o.y}px) scale(1)` : `translate(${o.x}px, ${o.y}px) scale(0)`,
                transformBox: "fill-box",
                transformOrigin: "center",
                transition: `opacity 600ms ${i * 110}ms, transform 700ms cubic-bezier(0.34,1.56,0.64,1) ${i * 110}ms`,
              }}
              data-testid={`map-dot-${i}`}
            >
              {/* Pulse ring */}
              <circle
                r={r * 2.2}
                fill={fill}
                opacity="0.25"
                style={{ animation: mounted ? `dotPulse 2.2s ease-out ${i * 0.35}s infinite` : "none", transformOrigin: "center" }}
              />
              {/* Active hover ring */}
              {isActive && <circle r={r * 1.7} fill="none" stroke="#FB923C" strokeWidth="2" opacity="0.85" />}
              {/* Core */}
              <circle r={r} fill={fill} filter="url(#glow)" />
              <circle r={r * 0.45} fill="white" />
            </g>
          );
        })}

        {/* Labels */}
        {projectedWithLabels.map((o, i) => {
          return (
            <g
              key={`label-${i}`}
              style={{ opacity: mounted ? 1 : 0, transition: `opacity 700ms ${300 + i * 120}ms` }}
              onClick={() => onSelectOffice && onSelectOffice(i)}
              className="cursor-pointer"
            >
              <text
                x={o.labelX}
                y={o.labelY}
                textAnchor={o.anchor}
                fill="rgba(255,255,255,0.9)"
                fontSize="13"
                fontWeight="700"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {shortLabel(o.name || o.title)}
              </text>
              <text x={o.labelX} y={o.labelY + 14} textAnchor={o.anchor} fill="rgba(255,255,255,0.5)" fontSize="10">
                {o.is_head_office ? "Head office" : "Branch"}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Floating legend */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-white/60 px-2">
        <span className="inline-flex items-center gap-1.5"><Crown size={10} weight="fill" className="text-[#FB923C]" /> Head</span>
        <span className="inline-flex items-center gap-1.5"><MapPin size={10} weight="fill" className="text-[#0EA5E9]" /> Branch</span>
      </div>
    </div>
  );
}

function shortLabel(name = "") {
  // Strip suffix noise and any city-suffix separated by an en-dash so labels
  // stay legible on a small SVG. e.g. "Manufacturing Unit I — Haridwar"
  // becomes "Mfg Unit I".
  let s = name.replace(/\s*(Office|Branch|Sales)$/i, "").trim();
  // Cut off " — City" tail
  s = s.replace(/\s+[—–-]\s+.*$/, "").trim();
  // Compact common phrases
  s = s.replace(/^Manufacturing\s+Unit/i, "Mfg Unit")
       .replace(/^Corporate/i, "Corporate")
       .replace(/^Regional/i, "Regional");
  return s.length > 22 ? s.slice(0, 20) + "…" : s;
}
